const REDACTED = 'REDACTED';
const SENSITIVE_QUERY_PARAMS = [
  'lt',
  'authToken',
  'token',
  'JwtToken',
  'loginToken',
  'id_token',
  'access_token',
  'refresh_token',
  'apiKey',
  'api_key',
  'key',
];
const SENSITIVE_QUERY_PARAM_SET = new Set(SENSITIVE_QUERY_PARAMS.map((param) => param.toLowerCase()));
const SENSITIVE_FIELD_EXACT_KEYS = new Set(['authorization', 'cookie', 'setcookie']);
const SENSITIVE_FIELD_KEY_PARTS = ['token', 'secret', 'password', 'apikey'];
const URL_FIELD_KEYS = new Set(['url', 'referrer', 'href', 'currentPage', 'queryString']);
const RAW_SENSITIVE_PARAM_REGEX =
  /(^|[?&])(lt|authToken|token|JwtToken|loginToken|id_token|access_token|refresh_token|apiKey|api_key|key)=([^&#\s]*)/gi;
const RAW_SENSITIVE_PARAM_MARKER_REGEX =
  /(^|[?&])(lt|authToken|token|JwtToken|loginToken|id_token|access_token|refresh_token|apiKey|api_key|key)=/i;
const ENCODED_SENSITIVE_PARAM_REGEX =
  /(%26|%3F)(lt|authToken|token|JwtToken|loginToken|id_token|access_token|refresh_token|apiKey|api_key|key)%3D((?:(?!%26|%23|[&#\s]).)*)/gi;
const ENCODED_SENSITIVE_PARAM_MARKER_REGEX =
  /(%26|%3F)(lt|authToken|token|JwtToken|loginToken|id_token|access_token|refresh_token|apiKey|api_key|key)%3D/i;
const STRIPE_CLIENT_SECRET_REGEX = /\b(?:pi|seti|cs)_\w+_secret_[A-Za-z0-9]+\b/g;
const STRIPE_CLIENT_SECRET_MARKER_REGEX = /\b(?:pi|seti|cs)_\w+_secret_[A-Za-z0-9]+\b/;

const hasSensitiveQueryCandidate = (input) =>
  RAW_SENSITIVE_PARAM_MARKER_REGEX.test(input) || ENCODED_SENSITIVE_PARAM_MARKER_REGEX.test(input);

const hasInlineSensitiveFieldCandidate = (input) => {
  const lowerInput = input.toLowerCase();
  return ['authorization', 'cookie', 'token', 'secret', 'password', 'api'].some((marker) => lowerInput.includes(marker));
};

const hasSensitiveCandidate = (input) =>
  hasSensitiveQueryCandidate(input) || STRIPE_CLIENT_SECRET_MARKER_REGEX.test(input) || hasInlineSensitiveFieldCandidate(input);

const isSensitiveFieldKey = (key) => {
  const normalizedKey = String(key)
    .toLowerCase()
    .replaceAll(/[^a-z0-9]/g, '');
  return SENSITIVE_FIELD_EXACT_KEYS.has(normalizedKey) || SENSITIVE_FIELD_KEY_PARTS.some((part) => normalizedKey.includes(part));
};

const isWhitespace = (char) => char === ' ' || char === '\n' || char === '\r' || char === '\t';

const isInlineKeyChar = (char) =>
  (char >= 'a' && char <= 'z') ||
  (char >= 'A' && char <= 'Z') ||
  (char >= '0' && char <= '9') ||
  char === '_' ||
  char === '-' ||
  char === '.' ||
  char === '"' ||
  char === "'";

const isInlineValueBoundary = (char) => !char || isWhitespace(char) || char === ',' || char === '}' || char === ']' || char === '&';

const stripInlineKeyQuotes = (key) => {
  let output = key;
  if (output.startsWith('"') || output.startsWith("'")) {
    output = output.slice(1);
  }
  if (output.endsWith('"') || output.endsWith("'")) {
    output = output.slice(0, -1);
  }
  return output;
};

const readInlineKey = (input, separatorIndex, cursor) => {
  let keyEnd = separatorIndex - 1;
  while (keyEnd >= cursor && isWhitespace(input[keyEnd])) {
    keyEnd -= 1;
  }

  let keyStart = keyEnd;
  while (keyStart >= cursor && isInlineKeyChar(input[keyStart])) {
    keyStart -= 1;
  }
  keyStart += 1;

  if (keyStart > keyEnd) {
    return null;
  }

  return stripInlineKeyQuotes(input.slice(keyStart, keyEnd + 1));
};

const readInlineRedaction = (input, valueStart) => {
  const quote = input[valueStart] === '"' || input[valueStart] === "'" ? input[valueStart] : '';
  if (quote) {
    const quoteEnd = input.indexOf(quote, valueStart + 1);
    return {
      text: `${quote}${REDACTED}${quote}`,
      valueEnd: quoteEnd === -1 ? input.length : quoteEnd + 1,
    };
  }

  let valueEnd = valueStart;
  if (input.slice(valueStart, valueStart + 6).toLowerCase() === 'bearer' && isWhitespace(input[valueStart + 6])) {
    valueEnd += 6;
    while (valueEnd < input.length && isWhitespace(input[valueEnd])) {
      valueEnd += 1;
    }
  }
  while (valueEnd < input.length && !isInlineValueBoundary(input[valueEnd])) {
    valueEnd += 1;
  }
  return { text: REDACTED, valueEnd };
};

const redactInlineSensitiveFields = (input) => {
  if (!hasInlineSensitiveFieldCandidate(input)) {
    return input;
  }

  let output = '';
  let cursor = 0;
  let index = 0;
  while (index < input.length) {
    const separator = input[index];
    if (separator !== ':' && separator !== '=') {
      index += 1;
      continue;
    }

    const key = readInlineKey(input, index, cursor);
    if (!key || !isSensitiveFieldKey(key)) {
      index += 1;
      continue;
    }

    let valueStart = index + 1;
    while (valueStart < input.length && isWhitespace(input[valueStart])) {
      valueStart += 1;
    }
    if (valueStart >= input.length) {
      index += 1;
      continue;
    }

    const redaction = readInlineRedaction(input, valueStart);
    output += input.slice(cursor, valueStart) + redaction.text;
    cursor = redaction.valueEnd;
    index = redaction.valueEnd;
  }

  return cursor === 0 ? input : `${output}${input.slice(cursor)}`;
};

const formatParsedUrl = (input, url) => {
  if (/^[a-z][a-z\d+\-.]*:\/\//i.test(input)) {
    return url.toString();
  }
  if (input.startsWith('//')) {
    return `//${url.host}${url.pathname}${url.search}${url.hash}`;
  }
  if (input.startsWith('?')) {
    return `${url.search}${url.hash}`;
  }
  if (input.startsWith('/')) {
    return `${url.pathname}${url.search}${url.hash}`;
  }
  return `${url.pathname.replace(/^\//, '')}${url.search}${url.hash}`;
};

const isUrlShapedInput = (input) =>
  !/\s/.test(input) &&
  (/^[a-z][a-z\d+\-.]*:\/\//i.test(input) ||
    input.startsWith('//') ||
    input.startsWith('?') ||
    input.startsWith('/') ||
    /^[^?#]+[?#]/.test(input));

export const scrubSensitiveUrl = (input) => {
  if (!input || typeof input !== 'string' || !hasSensitiveCandidate(input)) {
    return input;
  }

  let output = input;

  if (isUrlShapedInput(input)) {
    try {
      const base = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost';
      const url = new URL(input, base);
      let changed = false;
      for (const key of url.searchParams.keys()) {
        if (SENSITIVE_QUERY_PARAM_SET.has(key.toLowerCase())) {
          url.searchParams.set(key, REDACTED);
          changed = true;
        }
      }
      if (changed) {
        output = formatParsedUrl(input, url);
      }
    } catch {
      // Keep the original shape and use regex fallbacks below.
    }
  }

  output = output.replaceAll(RAW_SENSITIVE_PARAM_REGEX, (_match, separator, param) => `${separator}${param}=${REDACTED}`);
  output = output.replaceAll(ENCODED_SENSITIVE_PARAM_REGEX, (_match, separator, param) => `${separator}${param}%3D${REDACTED}`);
  output = output.replaceAll(STRIPE_CLIENT_SECRET_REGEX, REDACTED);
  output = redactInlineSensitiveFields(output);

  return output;
};

export const scrubSensitiveUrlFromEvent = (event, depth = 0) => {
  if (depth > 6 || !event || typeof event !== 'object') {
    return;
  }

  if (Array.isArray(event)) {
    for (const item of event) {
      scrubSensitiveUrlFromEvent(item, depth + 1);
    }
    return;
  }

  for (const key of Object.keys(event)) {
    const value = event[key];
    if (typeof value === 'string') {
      if (isSensitiveFieldKey(key)) {
        event[key] = REDACTED;
        continue;
      }
      if (URL_FIELD_KEYS.has(key) || hasSensitiveCandidate(value)) {
        event[key] = scrubSensitiveUrl(value);
      }
    } else if (value && typeof value === 'object') {
      if (key === 'queryString' && !Array.isArray(value)) {
        for (const qsKey of Object.keys(value)) {
          const qsValue = value[qsKey];
          if (SENSITIVE_QUERY_PARAM_SET.has(qsKey.toLowerCase())) {
            value[qsKey] = REDACTED;
          } else if (typeof qsValue === 'string') {
            value[qsKey] = scrubSensitiveUrl(qsValue);
          }
        }
        continue;
      }
      scrubSensitiveUrlFromEvent(value, depth + 1);
    }
  }
};
